/**
 * The ladder envelope (#180) on the client, and the helpers that let a
 * rendered value find its claim (#183). Mirrors `agent/common/ladder.py`.
 */

export interface ClaimWindow {
  start?: string | null;
  end?: string | null;
  preset?: string | null;
  field?: string | null;
}

export interface Grounding {
  metric?: string | null;
  metric_qid?: string | null;
  perspective: string;
  window?: ClaimWindow | null;
  filters?: Array<Record<string, any>>;
  dimensions?: string[];
  /** The dimension values keying this cell. */
  row?: Record<string, any> | null;
  /** Output column → grain for the row's time buckets (`order_date_month` → 'month'): a period, not a dimension. */
  time_columns?: Record<string, string>;
  sub_task_id?: string | null;
  sql?: string | null;
  source?: string | null;
  definition?: string | null;
}

export interface Claim {
  id: string;
  span?: import('../../types/knowledge').Span & { quote: string };
  session_id?: string | null;
  statistic?: 'delta';
  label?: string | null;
  value?: any;
  format?: string | null;
  grounding?: Grounding | null;
  confidence: number;
  confidence_basis: string;
  verdict: 'derived' | 'unknown' | string;
  caveats?: string[];
}

export interface TableClaims {
  sub_task_id: string;
  kind: 'table' | 'series';
  columns: string[];
  metric_columns: string[];
  dimension_columns: string[];
  row_count?: number | null;
  rows_carried: number;
  claims: Claim[];
  sql?: string | null;
  /** #1489: which of this table's cells ARE the answer — indices and names only, no values. */
  answer?: AnswerShape;
  /** #2154: what a column is, by column: a parent-grain fact's value. Absent when no column has one. */
  column_notes?: Record<string, string>;
}

/** #1489: answer cell (r, k) is the table's row `from_rows[r]`, column `sources[k]` (its year when in `years`). */
export interface AnswerShape {
  columns: string[];
  sources: string[];
  from_rows: number[];
  years: string[];
  rows_asked: 'all' | 'one';
  ranked_by?: string | null;
}

export interface AnswerContext {
  metric: string; claims_count: number; owner: string | null; watching: Array<{ id: string; name: string }>;
  /** The viewer's topics tied to the metric (#576); null when they could not be read. */
  topics_count?: number | null;
  session_id: string; significance: { kind: string; z?: number | null };
  claims_note?: string; owner_note?: string; watching_note?: string;
}
/** What the router decided for an answer (#760); a clarifier carries no routes, its question and the options. */
export interface AnswerRoute {
  route: string | null;
  routes: string[];
  confidence: number | null;
  reason: string;
  decided_by: 'rule' | 'model' | 'unread' | 'fallback' | 'loop';
  clarifier?: string;
  options?: string[];
}

/** A knowledge, help or decline answer (#764): what people said (byte-exact quotes with their speaker), a help
 *  article, or the strict decline and its offers. Or a Writer's reply (#1667 small talk, #1668 a help question no
 *  article answers): its words, the facts it cited and the starter questions — read back by the server for whoever
 *  reads it, so a reader who may not see the words gets none. */
export interface SpecialistBlock {
  kind: 'knowledge' | 'help' | 'decline' | 'talk' | 'check';
  words?: string[];
  cites?: string[];
  status?: string;
  /** A doubt's reply (#1671): how the earlier number was computed, code-written, one line each. */
  computed?: string[];
  /** A doubt's reply: the earlier answer's scope, which its options are asked about. */
  context?: import('../../apis').ThreadContext | null;
  topics?: Array<{ id: string; title?: string; summary?: string | null; tier: string }>;
  quotes?: Array<{ text?: string; speaker?: string | null; said_at?: string | null; where?: string | null; tier: string;
    span: { document_id: string; content_hash: string; start: number; end: number } }>;
  decisions?: Array<{ id: string; text?: string; decided_by?: string | null; decided_at?: string | null; tier: string }>;
  below_bar?: number;
  article?: { id: string; title: string; answer: string; where: string; source: string } | null;
  message?: string;
  /** A declined answer (#1174): why, from the closed list, and that reason's one fixed sentence. */
  gap?: string;
  detail?: string;
  /** `note` (#1671): the asker writes what they expected, saved as a note on the earlier answer — never re-asked. */
  offers?: Array<{ text: string; question: string; route: string; metric?: string; id?: string; note?: boolean }>;
}

/** The perspective pick an answer honoured (#767); absent on a route that ignores it (knowledge, help, decline). */
export interface AnswerScope {
  pick: string;
  source: 'user' | 'objective' | 'auto';
  used: { id: string; name: string }[];
  /** The metric named outside the pick, and where it lives: the answer asks which to use. */
  not_in_pick?: { term: string; picked: { id: string; name: string }; homes: { id: string; name: string }[] };
  /** A Refresh (#762): the answer ran in the earlier analysis's perspective (`used`), not the pick. */
  from_earlier?: boolean;
}

/** One catalog entry (#761): what the catalog holds about a perspective, metric, dimension, table or definition. */
export interface CatalogEntry {
  kind: 'perspective' | 'metric' | 'dimension' | 'table' | 'definition';
  id: string;
  name: string;
  perspectives: { id: string; name: string }[];
  description?: string | null;
  formula?: string | null;
  status: 'published' | 'draft';
  owner?: string | null;
  version?: string | null;
  refresh?: string | null;
}

/** A catalog answer (#761), written for this reader from its references: entries, same-name conflicts, a follow-up. */
export interface CatalogBlock {
  kind: 'catalog';
  refs: { kind: CatalogEntry['kind']; id: string }[];
  listing?: CatalogEntry['kind'] | null;
  topic?: string[];
  drafts_only?: boolean;
  refused?: string | null;
  entries?: CatalogEntry[];
  conflicts?: { name: string; ids: string[] }[];
  follow_up?: string | null;
  /** The re-read could not run now (#761 r2): nothing of the stored answer is shown. */
  unread?: boolean;
  drafts_asked?: boolean;
}

/** One earlier analysis on a Prior work list (#911): where it is, whose, when, the window it answered for, what it
 *  found as disclosed to this reader, and what a Refresh would run (absent: no Refresh). */
export interface PriorWorkListItem {
  ref: { source: 'own' | 'shared' | 'report'; id: string; turn_id: string | null };
  kind?: string;
  title?: string;
  question?: string | null;
  when?: string | null;
  href?: string;
  mine?: boolean;
  by?: string | null;
  route?: string | null;
  metrics?: string[];
  window?: { start?: string | null; end?: string | null; preset?: string | null } | null;
  verdict?: { headline: string | null; policy?: { hidden_count?: number; raise_to?: string | null } | null } | null;
  refresh?: { window: { start?: string | null; end?: string | null; preset?: string | null }; perspective: string } | null;
  match?: 'same' | 'related' | null;
  found?: string | null;
}

/** Earlier analyses asked for explicitly (#911): the Prior work agent's list, read for this reader, and what people
 *  said beside it when the question asked that too (`said`). */
export interface PriorWorkListBlock {
  kind: 'prior_work';
  refs: Array<{ source: string; id: string; turn_id: string | null; match?: string | null }>;
  about?: string[];
  unread?: boolean;
  analyses?: PriorWorkListItem[];
  /** Could not read them, or nothing found: the sentence every surface shows (null when there is a list). */
  empty?: string | null;
  /** With nothing found, the offer to start one: its sentence, and the question it asks when a metric was named. */
  start?: { question: string | null; text: string } | null;
  said?: (Omit<SpecialistBlock, 'kind' | 'article' | 'message' | 'offers'> & { empty?: string | null }) | null;
}

/** The earlier answer of the thread an answer used (#1807, F2), as read for this viewer now: each side by reference
 *  with its label, and the line that says it — code words; the trace keeps references only. */
export interface EarlierBlock {
  how: 'ordinal' | 'named' | 'compared' | 'chosen';
  refs: { role: 'anchor' | 'left' | 'right'; session_id: string; binding?: { dimension: string; value: string | number };
          label?: string; position?: number }[];
  said: [number, number][];
  line?: string;
}

/** One default an answer stated (#1808, F3), as the server read it for this viewer: the chip's label, code's sentence
 *  and the server-issued options (a click sends `{session, default: id, option}`). */
export interface DefaultItem {
  id: string;
  kind: 'window' | 'grain' | 'dimension' | 'reading' | 'plain';
  perspective?: string | null;
  label: string;
  /** #1818: a reading item's `by` adds `source | not_default | ask` (the reading question) and `unsettled` (the one
   *  label-free form, `id: "reading"`, no options). */
  by: 'global' | 'fit' | 'asked' | 'authored' | 'earlier' | 'chosen' | 'source' | 'not_default' | 'ask' | 'unsettled';
  sentence: string;
  options: { option: number; label: string; current?: boolean }[];
  /** The sub-tasks the item decided (a window per group of parts is named `window:<first spec>`). */
  specs?: string[];
  /** #1818: a reading item's decision state, the reading it used (absent for the question) and the default. */
  state?: 'open' | 'provisional' | 'kept_both';
  used?: string;
  default?: string;
}

/** The defaults an answer stated (#1808): its session, every item, and whether this viewer may change them. */
export interface DefaultsBlock {
  kind: 'defaults';
  session: string;
  items: DefaultItem[];
  changeable?: boolean;
}

export interface LadderAnswer {
  route?: AnswerRoute | null;
  /** #1808 (F3): every default this answer stated, with one click to change it. */
  defaults?: DefaultsBlock | null;
  /** #1807 (F2): the earlier answer of the thread this one used. */
  earlier?: EarlierBlock | null;
  specialist?: SpecialistBlock | null;
  prior_work?: PriorWorkListBlock | null;
  scope?: AnswerScope | null;
  catalog?: CatalogBlock | null;
  /** The objective the question was asked inside (#766), by reference: the viewer's own access renders it. */
  objective?: { id: string } | null;
  delivery?: { revision: number; context_status: 'pending' | 'ready' | 'unavailable'; deadline?: number; checks?: Record<string, string> };
  context?: AnswerContext;
  version: number;
  question: string;
  perspective: string;
  status: string;
  /** What this answer was scoped to — the next question in the thread inherits it. */
  thread_context?: import('../../apis').ThreadContext | null;
  /** WHAT NEXT (#188): `next_note` says why there is nothing to do. */
  next_note?: string | null;
  /** What the viewer's evidence policy hid (#187): hidden counts, the withheld-WHAT-NEXT note, the next bar. */
  policy?: { policy: string; bar?: string | null; hidden_count?: number; why_hidden?: number; next_hidden?: number; next_withheld?: string | null; raise_to?: string | null; raised?: boolean } | null;
  trace_session_id?: string | null;
  thread_id?: string | null;
  what: {
    headline?: string | null;
    narrative?: string | null;
    tiles: Claim[];
    tables: TableClaims[];
    highlights: string[];
  };
  why?: any;
  next?: Array<{ text: string; kind: string; anchored_to?: string | null }> | null;
  caveats?: string[];
}

/** The i-th key-number tile's claim, if the envelope carries one. */
/** A loop answer with numbers that also read what people said (#2203): the answer card carries both, so neither is lost. */
export function knowledgeBesideNumbers(result: { synthesis?: any; diagnose?: any; ladder?: any; workerResults?: Array<{ status?: string }> } | null | undefined): boolean {
  const ladder = result?.ladder as LadderAnswer | null | undefined;
  return !!result?.synthesis?.loop && !result?.diagnose && ladder?.specialist?.kind === 'knowledge'
    && (result?.workerResults || []).some((w) => w.status === 'done');
}

export function tileClaim(ladder: LadderAnswer | null | undefined, index: number): Claim | null {
  return ladder?.what?.tiles?.[index] ?? null;
}

/** The claims behind one worker's table, if the envelope carries them. */
/** #2154: a column's note, or null. A column named like an Object member (`constructor`) has none. */
export function columnNote(table: TableClaims | null | undefined, column: string): string | null {
  const note: unknown = table?.column_notes?.[column];
  return typeof note === 'string' && note ? note : null;
}

export function tableClaims(ladder: LadderAnswer | null | undefined, subTaskId: string): TableClaims | null {
  return ladder?.what?.tables?.find((t) => t.sub_task_id === subTaskId) ?? null;
}

/** The claim for one cell: a metric column in a preview row. Null for dimension cells. */
export function cellClaim(table: TableClaims | null | undefined, rowIndex: number, column: string): Claim | null {
  if (!table || !table.metric_columns.includes(column)) return null;
  const id = `${table.sub_task_id}:${rowIndex}:${column}`;
  return table.claims.find((c) => c.id === id) ?? null;
}

/**
 * #1489: the claim behind answer cell (r, k). A metric or derived cell is its frame cell's claim; a dimension cell
 * beside a metric has none (as in the table); a dimension-only answer ("which X") is grounded by its row's claim on
 * the column the rows are ranked by, else its row's first metric.
 */
export function answerCellClaim(table: TableClaims | null | undefined, r: number, k: number): Claim | null {
  const a = table?.answer;
  if (!table || !a) return null;
  const row = a.from_rows[r];
  const source = a.sources[k];
  if (row === undefined || source === undefined) return null;
  if (table.metric_columns.includes(source)) return cellClaim(table, row, source);
  if (a.sources.some((s) => table.metric_columns.includes(s))) return null;
  const by = a.ranked_by && table.metric_columns.includes(a.ranked_by) ? a.ranked_by : table.metric_columns[0];
  return by ? cellClaim(table, row, by) : null;
}

/**
 * #1489: a year column's value as its year. The year rule is the server's alone (`answer_shape._year`): a column is
 * in `years` only when every one of its buckets passed it, whatever its format or offset, so the client takes the
 * year the bucket leads with and never judges the bucket again; undefined (fail closed) for a value that is not one.
 */
export function bucketYear(value: unknown): number | null | undefined {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isInteger(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const m = /^(\d{4})(?![0-9])/.exec(value);
  return m ? Number(m[1]) : undefined;
}

export interface AnswerCell { value: unknown; claim: Claim | null }

/**
 * #1489: the answer table rendered from the frame the card already holds (`rows`, by index) — never a second copy of
 * a value. Null (the table alone is shown) when the shape does not fit the frame: an index past the rows or out of
 * order, a source the frame lacks, a year that is not a bucket. It fails closed.
 */
export function answerTable(table: TableClaims | null | undefined, columns: string[], rows: any[]):
  { columns: string[]; rows: AnswerCell[][] } | null {
  const a = table?.answer;
  if (!table || !a || !a.sources.length || !a.from_rows.length) return null;
  if (!a.sources.every((s) => columns.includes(s))) return null;
  const out: AnswerCell[][] = [];
  for (let r = 0; r < a.from_rows.length; r += 1) {
    const i = a.from_rows[r];
    if (!Number.isInteger(i) || i < 0 || i >= rows.length || (r > 0 && i <= a.from_rows[r - 1])) return null;
    const row = rows[i] || {};
    const cells: AnswerCell[] = [];
    for (let k = 0; k < a.sources.length; k += 1) {
      let value: unknown = row[a.sources[k]];
      if (a.years.includes(a.sources[k])) {
        const year = bucketYear(value);
        if (year === undefined) return null;
        value = year;
      }
      cells.push({ value, claim: answerCellClaim(table, r, k) });
    }
    out.push(cells);
  }
  return { columns: a.columns, rows: out };
}

/**
 * The claim a chart point stands for: the row whose dimension values match
 * the datum, for the metric the datum carries.
 */
/** What a chart draws, read from its spec: the one metric column it encodes, and the fold discriminator if it folds. */
export interface ChartMetricHint {
  metric?: string | null;
  discriminator?: string | null;
}

/**
 * Read the metric a Vega-Lite spec encodes. A generated chart keeps every
 * column of its row on each datum, so the datum alone cannot say which one
 * is drawn; the spec can — the field on its positional/size/colour
 * channels, or the `fold` discriminator for a multi-series chart.
 */
export function encodedMetric(spec: any, table: TableClaims): ChartMetricHint {
  if (!spec || typeof spec !== 'object') return {};
  const layers = [spec, ...((spec.layer as any[]) || [])];
  const fields = new Set<string>();
  let discriminator: string | null = null;
  for (const l of layers) {
    for (const ch of Object.values(l?.encoding || {}) as any[]) {
      const f = ch && typeof ch === 'object' ? ch.field : null;
      if (typeof f === 'string') fields.add(f);
    }
    for (const t of (l?.transform as any[]) || []) {
      if (t && Array.isArray(t.fold)) discriminator = (Array.isArray(t.as) && typeof t.as[0] === 'string') ? t.as[0] : 'key';
    }
  }
  const metrics = table.metric_columns.filter((m) => fields.has(m));
  return { metric: metrics.length === 1 ? metrics[0] : null, discriminator };
}

export function datumClaim(table: TableClaims | null | undefined, datum: Record<string, any>,
                           hint: ChartMetricHint = {}): Claim | null {
  if (!table || !datum) return null;
  // Which metric this point stands for: the fold discriminator names it on
  // a multi-series chart; the spec's encoded field names it on a chart
  // drawn from a multi-metric row; a datum with exactly one metric column
  // needs neither. Anything else is ambiguous — no claim rather than the
  // wrong one.
  const isMetric = (v: unknown): v is string => typeof v === 'string' && table.metric_columns.includes(v);
  const discriminators = [hint.discriminator, 'metric', 'series', 'key', 'variable', 'measure'].filter(Boolean) as string[];
  const named = discriminators.map((k) => datum[k]).find(isMetric) || null;
  const present = table.metric_columns.filter((m) => m in datum);
  const metric = named || (isMetric(hint.metric) ? hint.metric : null) || (present.length === 1 ? present[0] : null);
  if (!metric) return null;
  // Every dimension the table carries must be on the datum and agree with the
  // row: an aggregated chart drops dimensions, and a datum that could stand
  // for several rows stands for none.
  const matches = table.claims.filter((c) => {
    if (c.grounding?.metric !== metric) return false;
    const row = c.grounding?.row || {};
    return table.dimension_columns.every((d) => d in datum && String(datum[d]) === String(row[d]));
  });
  return matches.length === 1 ? matches[0] : null;
}

/**
 * A chart point's claim: the bundle names the table it was drawn from
 * (`upstream_id`), and only that table is searched. Two sub-tasks can share
 * a metric and dimensions under different windows or filters; searching
 * every table would let a click on the second chart open the first one's
 * number. A bundle with no upstream is a chart with no number behind it.
 */
export function bundleClaim(ladder: LadderAnswer | null | undefined,
                            bundle: { upstream_id?: string | null; visualization?: { vega_lite?: any } | null } | null | undefined,
                            datum: Record<string, any>): Claim | null {
  const upstream = bundle?.upstream_id;
  if (!upstream) return null;
  const table = tableClaims(ladder, upstream);
  if (!table) return null;
  return datumClaim(table, datum, encodedMetric(bundle?.visualization?.vega_lite, table));
}

/** A stable JSON form: keys sorted at every level, so order never changes identity. */
export function canonical(v: any): string {
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  if (v && typeof v === 'object') {
    return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${canonical(v[k])}`).join(',')}}`;
  }
  return JSON.stringify(v ?? null);
}

/**
 * One key per number, stable across renders and reloads: what a note is
 * bound to. Metric, window, the row's dimension values — and the query's
 * own filters, because "orders, last week" for the US and for Canada are
 * two numbers with the same metric, window and (empty) row. Filters are
 * canonicalised and sorted so the same scope written in another order is
 * still the same number.
 */
/**
 * The key travels as an annotation's `source_ref` and back as a query
 * parameter, both capped server-side (SOURCE_REF_MAX_LEN = 1000). A key is
 * kept readable up to this length; a longer identity (long text dimensions,
 * several filters) keeps its readable head and hashes its tail.
 */
export const CLAIM_KEY_MAX = 500;

/** FNV-1a, twice with different offsets → 16 hex chars. Deterministic, no BigInt. */
function fnv1a(str: string, offset: number): string {
  let h = offset >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}
export function identityHash(s: string): string {
  return fnv1a(s, 0x811c9dc5) + fnv1a(s, 0x01000193 ^ 0x811c9dc5);
}

/** How to ask for the notes ON a number.
 *
 *  A note is ABOUT a metric over a window. How it is ADDRESSED depends on the surface that
 *  wrote it: the app and the extension mint a claim key (`source_ref`), MCP stamps the
 *  whole-window aggregate's key when a note names its window (`qid|start|end|{}`) and none
 *  otherwise, while Slack writes a scope — `{metrics, perspective}` plus the window — and no
 *  key at all. Reading by the claim key alone therefore finds only what was written on the
 *  number itself, and a colleague's note left in Slack was invisible on the very number it
 *  was about.
 *
 *  So a number asks the way the walk asks: ranked match on metric + window (`/api/annotations`
 *  ranked mode). The claim key is still read alongside it — a note bound to one exact cell of
 *  one table is narrower than the metric, and stays findable.
 *
 *  Returns `null` when the claim has no catalog metric or no dated window, which is exactly
 *  when a ranked match would be meaningless rather than empty. */
export function noteQueryForClaim(claim: Claim): NoteQuery | null {
  const g = claim.grounding;
  if (!g) return null;
  const metric = g.metric_qid;
  const w = g.window || ({} as ClaimWindow);
  if (!metric || !w.start || !w.end) return null;
  // The DATE FIELD is part of which number this is — orders placed in August and orders
  // delivered in August are two numbers, and the claim key says so. An annotation's scope
  // has no way to name a date field, so ranked matching cannot tell them apart: a claim that
  // names one keeps its exact-key notes rather than borrow the other's.
  if (w.field) return null;
  // A point on a time series is ONE bucket of the window, not the window: clicking July on a
  // Jul–Aug chart would otherwise ask about both months. The row names the bucket, but a note
  // written in Slack never names a generated month column, so the cell predicate cannot rule
  // it out either — and a note about August under July is worse than no note at all. Such a
  // claim keeps its exact-key notes and asks nothing wider.
  // A date names a bucket whether or not the query generated a time column for it: grouping by
  // a raw `order_date` gives daily rows and an empty `time_columns`, and July 1 must not ask
  // about August.
  if (namesABucket(g)) return null;
  // Likewise a filter the cell cannot express. `region != West` pins the number to
  // everything-but-West, which no {dimension: value} cell can say — so a West note could not
  // be ruled out, and would sit under a total that excludes West by construction.
  if (!expressibleCell(g)) return null;
  return {
    metric,
    window_start: w.start,
    window_end: w.end,
    perspective: g.perspective || undefined,
    filters: g.filters && g.filters.length ? JSON.stringify(g.filters) : undefined,
    // The cell. Every row of a regional table is the same metric over the same window, so
    // matching the metric cannot be the whole test: a note about West is not about East.
    // An aggregate answers for its slice through its FILTERS rather than a row, and a number
    // filtered to East is as much "the East cell" as a row labelled East is.
    row: Object.keys(cellOf(g)).length ? JSON.stringify(cellOf(g)) : undefined,
  };
}

/** The cell a grounding names: its row, plus whatever its equality filters pin down. Both are
 *  written in the CATALOG's dimensions, which is how a note is scoped. */
const DATE_VALUE = /^\d{4}-\d{2}-\d{2}([T ]|$)/;
/** Does this cell name ONE bucket of the window? Either a generated time column, or a plain
 *  date sitting in the row. Such a claim keeps its exact-key notes and asks nothing wider:
 *  a note written elsewhere never names the bucket, so nothing could rule it out. */
export function namesABucket(g: Claim['grounding']): boolean {
  const row = (g?.row || {}) as Record<string, unknown>;
  if (Object.keys(g?.time_columns || {}).some((c) => c in row)) return true;
  // A date pinned by an equality FILTER names one bucket as surely as one sitting in the row:
  // `order_date = 2026-07-01` over a July–August window is the July 1 number.
  const values = [...Object.values(row), ...((g?.filters || []) as any[]).map((f) => f?.value)];
  return values.some((v) => typeof v === 'string' && DATE_VALUE.test(v));
}

/** The bucket's own date, for stamping a note written on it — the same rule `namesABucket`
 *  reads by, so the writer cannot file a July note under August while the reader knows better.
 *  Only a value that is WHOLLY a date: a comparison period reads "2026-09-01 → 2026-09-07",
 *  which the server would refuse as a timestamp. */
// A whole date, optionally with a time after T or a SPACE — the query executor returns
// "2026-09-05 00:00:00+00:00". Digits must follow the separator, which is what keeps a
// comparison period ("2026-09-01 → 2026-09-07") out: its space is followed by an arrow.
const WHOLE_DATE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;
export function bucketDate(g: Claim['grounding']): string | null {
  const row = (g?.row || {}) as Record<string, unknown>;
  const timed = Object.keys(g?.time_columns || {}).map((c) => row[c]);
  // EQUALITY only. `namesABucket` may read a `<` date and stop the claim asking wider — that
  // errs safe — but a bound is not a bucket, and stamping a note with `order_date < 2026-09-01`
  // would file an August note in September, outside the window it was written in.
  const pinned = ((g?.filters || []) as any[]).filter((f) => f && (!f.operator || f.operator === '=')).map((f) => f.value);
  const found = [...timed, ...Object.values(row), ...pinned].find((v) => typeof v === 'string' && WHOLE_DATE.test(v));
  if (!found) return null;
  // Clamped to the window the number was about. A weekly bucket is labelled with its START, so
  // an August query's first bucket reads 2026-07-27 — and a note stamped there lands in July,
  // under a month this number never covered and out of reach of the month it did.
  const day = (found as string).slice(0, 10);
  const start = g?.window?.start;
  const end = g?.window?.end;
  if (start && day < start) return start;
  if (end && day > end) return end;
  return found as string;
}

/** Can the cell say everything the grounding does? A predicate that is not an equality on a
 *  scalar has no place in a {dimension: value} cell, and a note it should have excluded would
 *  survive. The reader asks nothing wider rather than show a note about another slice. */
export function expressibleCell(g: Claim['grounding']): boolean {
  return ((g?.filters || []) as any[]).every((f) => f && f.dimension
    && (!f.operator || f.operator === '=') && f.value !== undefined && !Array.isArray(f.value));
}

export function cellOf(g: Claim['grounding']): Record<string, unknown> {
  const cell: Record<string, unknown> = { ...(g?.row || {}) };
  for (const f of (g?.filters || []) as any[]) {
    if (f && f.dimension && (!f.operator || f.operator === '=') && f.value !== undefined && !Array.isArray(f.value)) {
      cell[String(f.dimension)] = f.value;
    }
  }
  return cell;
}

export interface NoteQuery {
  metric: string;
  window_start: string;
  window_end: string;
  perspective?: string;
  filters?: string;
  row?: string;
}

export function claimKey(claim: Claim): string {
  if (claim.span) {
    const s = claim.span;
    const key = `span:${s.document_id}:${s.content_hash}:${s.start}:${s.end}`;
    return key.length <= CLAIM_KEY_MAX ? key : `span:#${identityHash(key)}`;
  }
  const g = claim.grounding;
  // An ungrounded number has no identity that outlives its answer: `tile:0`
  // is every answer's first tile. Nothing durable — a pin, a note — is ever
  // bound to this key (see NumberPopover / pinsSlice); it only names the
  // claim within the answer it came from.
  if (!g) return `unground:${claim.id}`;
  const row = g.row ? canonical(g.row) : '{}';
  const filters = (g.filters || []).map(canonical).sort();
  const scope = filters.length ? `|${filters.join(';')}` : '';
  // A catalog metric is one number wherever it is asked from: its qualified
  // id is the identity, so a note on it is found from every perspective. A
  // derived column has only an alias, and two perspectives can hand out the
  // same alias for different expressions — the perspective qualifies it.
  const metric = g.metric_qid || `${g.perspective || '?'}:${g.metric || '?'}`;
  // The window is dates when the query had them; otherwise the preset it
  // was asked with (YTD, MTD, a rolling window) — those are different
  // numbers, not the same number with no dates. The date field is part of
  // it too: "last 7 days" of order_date and of delivery_date differ.
  const w = g.window || ({} as ClaimWindow);
  const span = (w.start || w.end) ? `${w.start || ''}|${w.end || ''}` : `~${w.preset || ''}|`;
  const head = `${metric}|${w.field ? `${w.field}@` : ''}${span}`;
  const tail = `${row}${scope}${claim.statistic ? `|stat:${claim.statistic}` : ''}`;
  const key = `${head}|${tail}`;
  if (key.length <= CLAIM_KEY_MAX) return key;
  // The hash covers the WHOLE identity: the readable head may itself be cut.
  return `${head.slice(0, CLAIM_KEY_MAX - 20)}|#${identityHash(key)}`;
}

export function windowLabel(w: ClaimWindow | null | undefined): string {
  if (!w) return '';
  if (w.start && w.end) return `${w.start} → ${w.end}`;
  if (w.preset) return w.preset.replace(/_/g, ' ').toLowerCase();
  return w.start || w.end || '';
}

/** "metric · perspective · window · confidence" — the line under the number. */
export function groundingLine(claim: Claim): string {
  if (claim.span) return `${claim.span.document_id} · ${claim.span.content_hash} · bytes ${claim.span.start}–${claim.span.end}`;
  const g = claim.grounding;
  const parts: string[] = [];
  if (g?.metric_qid || g?.metric) parts.push(g.metric_qid || g.metric || '');
  if (g?.perspective) parts.push(g.perspective);
  const win = windowLabel(g?.window);
  if (win) parts.push(win);
  parts.push(`${Math.round((claim.confidence || 0) * 100)}% ${claim.confidence_basis || ''}`.trim());
  return parts.join(' · ');
}

/** The row's dimension values as "region West · store 12". */
export function rowLabel(claim: Claim): string {
  const row = claim.grounding?.row || {};
  return Object.entries(row).map(([k, v]) => `${k} ${v}`).join(' · ');
}
